import { useAuth } from '@/context/auth';
import { useUserData } from '@/context/auth/user-data-context';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import ProfilePicto from '@public/profile-picto.svg';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSelectedLayoutSegments } from 'next/navigation';
import {
    NAVIGATION_ACTIVE_ITEM_CLASS,
    NAVIGATION_ITEM_CLASS,
    NAVIGATION_LAST_ITEM_CLASS,
    NavigationDropDown,
} from './navigation-dropdown';

type Props = {
    className?: string;
    iconOnly?: boolean;
    margin?: string;
    isScrolled?: boolean;
};

export default function AccountDropDown({
    className,
    iconOnly = false,
    margin = 'ml-auto',
    isScrolled = false,
}: Props) {
    const segments = useSelectedLayoutSegments();

    const { user, signin, signout } = useAuth();
    const { name, isSeller, isBuyer } = useUserData();

    const t = useTranslations('Header');
    const tNav = useTranslations('Navigation');

    const customClassName = `${className} flex justify-between items-center rounded-[7px] text-content-on-dark px-4 py-3 transition duration-300 cursor-pointer flex-shrink-0`;
    const listItemClassName = cn('whitespace-nowrap', NAVIGATION_ITEM_CLASS);
    const isAccountSectionActive = ['account', 'settings', 'transactions'].includes(segments[0]);

    if (user) {
        if (isSeller || isBuyer) {
            return (
                <NavigationDropDown
                    label={
                        <>
                            <Image src={ProfilePicto.src} alt="profile picto" width={24} height={24} />
                            {!iconOnly ? (
                                <span className="truncate whitespace-nowrap overflow-hidden max-w-[150px] ml-2">
                                    {name}
                                </span>
                            ) : null}
                        </>
                    }
                    isActive={isAccountSectionActive}
                    showChevron={!iconOnly}
                    triggerClassName={cn(className, 'ml-4')}
                    inactiveTriggerClassName={
                        isScrolled
                            ? 'bg-overlay-white-10 hover:bg-overlay-white-25'
                            : 'bg-overlay-white-10 hover:bg-overlay-white-15'
                    }
                    panelLayout="floating"
                    panelPositionClassName="right-0"
                    panelSizeClassName=""
                    panelClassName="w-auto md:max-w-[300px] lg:min-w-[230px] lg:max-w-[400px] max-h-[440px] sm:max-h-[initial] overflow-y-auto"
                >
                    {(close) => (
                        <>
                            {isBuyer ? (
                                <li>
                                    <Link
                                        className={
                                            segments[0] === 'transactions'
                                                ? NAVIGATION_ACTIVE_ITEM_CLASS
                                                : listItemClassName
                                        }
                                        href="/transactions"
                                        onClick={close}
                                    >
                                        {tNav('buyer.transactions')}
                                    </Link>
                                </li>
                            ) : null}
                            {isBuyer ? (
                                <li>
                                    <Link
                                        className={cn(
                                            segments.join('/').includes('licenses')
                                                ? NAVIGATION_ACTIVE_ITEM_CLASS
                                                : listItemClassName,
                                            'lg:hidden',
                                        )}
                                        href="/licenses"
                                        onClick={close}
                                    >
                                        {tNav('licenses')}
                                    </Link>
                                </li>
                            ) : null}
                            <li>
                                <Link
                                    className={
                                        segments[0] === 'account' ? NAVIGATION_ACTIVE_ITEM_CLASS : listItemClassName
                                    }
                                    href="/account"
                                    onClick={close}
                                >
                                    {t('accountSettings')}
                                </Link>
                            </li>
                            {isBuyer ? (
                                <li>
                                    <Link
                                        className={
                                            segments[0] === 'settings'
                                                ? NAVIGATION_ACTIVE_ITEM_CLASS
                                                : listItemClassName
                                        }
                                        href="/settings"
                                        onClick={close}
                                    >
                                        {t('technicalSettings')}
                                    </Link>
                                </li>
                            ) : null}
                            <li>
                                <button
                                    type="button"
                                    className={cn(listItemClassName, NAVIGATION_LAST_ITEM_CLASS, 'w-full')}
                                    onClick={() => {
                                        close();
                                        signout();
                                    }}
                                >
                                    {t('Sign out')}
                                </button>
                            </li>
                        </>
                    )}
                </NavigationDropDown>
            );
        }

        return (
            <button type="button" onClick={signout} className={iconOnly ? `${className} ${margin}` : customClassName}>
                <Image src={ProfilePicto.src} alt="profile picto" className="mr-2" width={24} height={24} />
                {!iconOnly ? <span>{t('Sign out')}</span> : ''}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={signin}
            className={iconOnly ? `${className} ${margin} flex-shrink-0` : customClassName}
        >
            <Image src={ProfilePicto.src} alt="profile picto" className="mr-2" width={24} height={24} />
            {!iconOnly ? <span>{t('Sign in')}</span> : ''}
        </button>
    );
}
